import React, { ForwardRefExoticComponent, RefAttributes, useContext, useState } from "react";
import { Box, createStyles, makeStyles, TextFieldProps, Theme } from "@material-ui/core";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import CustomDialog, { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomButton from "../../../components/Button/CustomButton";
import CustomSearchAutocomplete, {
  CustomSearchAutocompleteProps,
} from "../../../components/Autocomplete/CustomSearchAutoComplete";
import { TestSeriesContext } from "../../../contexts/TestSeriesContext";
import TesaTmbApi, { TestMethod } from "../../../api/services/TesaTmbApi";
import PagedResponse from "../../../api/models/PagedResponse";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import CustomAlert from "../../../components/Alert/CustomAlert";
import ConfidentialConfirmationDialog from "../../../components/Dialog/ConfidentialConfirmationDialog";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../functions/confidentialCheck.function";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    subHeader: {
      color: theme.palette.grey["700"],
    },
    item: {
      "& > *": {
        width: "100%",
      },
      paddingLeft: `${theme.spacing(2)}px !important`,
      paddingRight: `${theme.spacing(2)}px !important`,
      paddingTop: `${theme.spacing(0.5)}px !important`,
      paddingBottom: `${theme.spacing(0.5)}px !important`,
    },
    marginTop: {
      marginTop: theme.spacing(3),
    },
  }),
);

const TestMethodSearchAutocomplete = CustomSearchAutocomplete as ForwardRefExoticComponent<
  MergedProps<CustomSearchAutocompleteProps<TestMethod>, TextFieldProps> & RefAttributes<{ reset: () => void }>
>;

const TestSeriesMethodDialog = (props: CustomDialogProps) => {
  const { open, onClose } = props;
  const dialogContext = useContext(ConfirmationDialogContext);
  const tesaTmbApi: TesaTmbApi = new TesaTmbApi(dialogContext);
  const classes = useStyles(props);
  const [confidentialDialogOpen, setConfidentialDialogOpen] = useState(false);

  const { updateTestMethod, regainConfig } = useContext(TestSeriesContext);

  const yupValidationSchema = Yup.object().shape({
    testMethod: Yup.mixed().required("Please select"),
  });

  return (
    <>
      <CustomDialog fullWidth maxWidth="sm" onClose={() => onClose(false)} open={open}>
        <Box marginBottom={5}>
          <h2>TEST CONFIGURATION</h2>
          {regainConfig && (
            <>
              <h3 className={classes.subHeader}>
                Test Series reusing regained
                {regainConfig?.regainConfig === "SUBSTRATES" ? " substrate " : " product "}
                samples
              </h3>
              <p>
                When you continue to create a subsequent Test Series, this Test Series will be set to “In Execution” so
                that changes in the configurator and test table are disabled.
              </p>
            </>
          )}
          <CustomAlert
            severity="info"
            text="All test methods you can see in the suggestions are created by a team of administrators. 
You always have the option to choose between different test methods."
          />
        </Box>

        <Formik<{
          testMethod: TestMethod | null;
        }>
          initialValues={{ testMethod: null }}
          onSubmit={async (values): Promise<void> => {
            const regex = await fetchHighlyConfidentialRegex();
            if (values.testMethod && containsHighlyConfidentialInfo([values.testMethod.name], regex)) {
              setConfidentialDialogOpen(true);
            } else {
              updateTestMethod(values.testMethod);
              onClose(true);
            }
          }}
          validationSchema={yupValidationSchema}
        >
          {(
            formikProps: FormikProps<{
              testMethod: TestMethod | null;
            }>,
          ) => {
            const { errors, isSubmitting, touched, values, setFieldValue } = formikProps;
            return (
              <>
                <Form>
                  <Grid container justify="flex-start" direction="row" spacing={4}>
                    <Grid className={classes.item} item xs={12}>
                      <TestMethodSearchAutocomplete
                        autoFocus
                        error={!!(errors.testMethod && touched.testMethod)}
                        helperText={[errors.testMethod && touched.testMethod ? errors.testMethod : ""].join(" ")}
                        inputLabel="Choose Test Method"
                        mandatory
                        onChange={(_, value): void => {
                          setFieldValue("testMethod", value);
                        }}
                        map={(testMethod): { key: string; label: string } => {
                          return {
                            key: `${testMethod.id}-${testMethod.version}`,
                            label: testMethod.name,
                          };
                        }}
                        optionFetch={(term): Promise<PagedResponse<TestMethod>> =>
                          tesaTmbApi.fetchTestMethodVersions(term).then((response) => response.data)
                        }
                        value={values.testMethod}
                        freeze
                      />
                    </Grid>
                    <Grid item xs={10} />
                    <Grid className={classes.item} item xs={2}>
                      <CustomButton
                        className={classes.marginTop}
                        disabled={isSubmitting}
                        primary
                        text="Next"
                        type="submit"
                      />
                    </Grid>
                  </Grid>
                </Form>
                <ConfidentialConfirmationDialog
                  open={confidentialDialogOpen}
                  onCancel={() => setConfidentialDialogOpen(false)}
                  onProceed={() => {
                    updateTestMethod(values.testMethod);
                    setConfidentialDialogOpen(false);
                    onClose(true);
                  }}
                  objectName="test method"
                />
              </>
            );
          }}
        </Formik>
      </CustomDialog>
    </>
  );
};

export default TestSeriesMethodDialog;
